'use client';

import { InsightDataDto } from '@/shared/job/insight.dto';
import { Briefcase, GraduationCap } from 'lucide-react';
import { type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ApplicantCountCard } from './ApplicantCountCard';
import { AdViewsCard } from './AdViewsCard';
import { InsightBarCard } from './InsightBarCard';
import { InsightFrequencyCard } from './SkillFrequencyCard';
import { insightCopy } from './insightCopy';
import { InsightCompareCard } from './CompareSkillsCard';
import { ZangiaPlusMemberBadge } from './ZangiaPlusMemberBadge';
import { formatInsightSinceFromJobTime } from '@/features/jobs/lib/formatInsightSince';
import { useHasActiveZangiaPlus } from '@/features/jobs/hooks/useHasActiveZangiaPlus';
import { ZangiaPlusInsightViewMode } from '@/types/zangiaPlus.types';

const collapseTransition = {
  duration: 0.35,
  ease: [0.22, 1, 0.36, 1],
} as const;

export function ZangiaPlusInsightView({
  data,
  jobTime,
  isEnglish = false,
  collapsed = false,
  headerAction,
  animateCollapse = false,
  mode = 'member',
}: {
  data: InsightDataDto;
  jobTime?: number | string;
  isEnglish?: boolean;
  collapsed?: boolean;
  headerAction?: ReactNode;
  animateCollapse?: boolean;
  mode?: ZangiaPlusInsightViewMode;
}) {
  const isOpen = !collapsed;

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <ZangiaPlusMemberBadge />
        {headerAction}
      </div>
      {animateCollapse ? (
        <AnimatePresence initial={false}>
          {isOpen ? (
            <motion.div
              key="insight-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={collapseTransition}
              className="overflow-hidden"
            >
              <InsightBody
                data={data}
                jobTime={jobTime}
                isEnglish={isEnglish}
                mode={mode}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      ) : (
        <InsightBody
          data={data}
          jobTime={jobTime}
          isEnglish={isEnglish}
          mode={mode}
        />
      )}
    </div>
  );
}

function InsightBody({
  data,
  jobTime,
  isEnglish,
  mode,
}: {
  data: InsightDataDto;
  jobTime?: number | string;
  isEnglish: boolean;
  mode: ZangiaPlusInsightViewMode;
}) {
  const t = isEnglish ? insightCopy.en : insightCopy.mn;
  const eduHeaderTotal = data.edu.reduce((sum, item) => sum + item.count, 0);
  const expHeaderTotal = data.exp.reduce((sum, item) => sum + item.count, 0);
  const sinceLabel = formatInsightSinceFromJobTime(jobTime, isEnglish);
  const { hasPlus, isAuthLoading } = useHasActiveZangiaPlus();
  const isLockedPreview = mode === 'lockedPreview';
  const showPlusExclusive = !isLockedPreview && !isAuthLoading && hasPlus;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium leading-6 text-gray-900">
          {t.title}
        </h2>
        <p className="text-sm leading-5 text-gray-600">{t.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AdViewsCard
          title={t.adViews}
          totalLabel={t.adViewsTotal}
          views={data.hits}
        />
        <ApplicantCountCard
          title={t.applicants}
          totalLabel={t.total}
          last7Label={t.last7}
          yourCvOnlyLabel={t.yourCvOnly}
          total={data.total}
          last7Days={data.last7Days}
          sinceLabel={sinceLabel}
        />
      </div>

      {!isLockedPreview ? (
        <div className="hidden grid-cols-1 gap-4 sm:grid sm:grid-cols-2">
          <InsightBarCard
            title={t.education}
            icon={GraduationCap}
            items={data.edu}
            total={data.total}
            variant="edu"
            isEnglish={isEnglish}
            headerTotal={eduHeaderTotal}
          />
          <InsightBarCard
            title={t.experience}
            icon={Briefcase}
            items={data.exp}
            total={data.total}
            variant="exp"
            isEnglish={isEnglish}
            headerTotal={expHeaderTotal}
          />
        </div>
      ) : null}

      {showPlusExclusive ? (
        <>
          <InsightFrequencyCard
            title={t.skills}
            items={data.compatibility}
            isEnglish={isEnglish}
          />
          <InsightCompareCard data={data} isEnglish={isEnglish} />
        </>
      ) : null}
    </div>
  );
}
